import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { Column } from 'components/Table/Table'
import { format, isValid } from 'date-fns'
import { useOrders } from 'hooks/useOrders'
import { Order } from 'types/api/order'
import { ListingPage } from 'components/ListingPage/ListingPage'
import { useGetUserPlaces } from 'hooks/usePlaces'
import {
  createSearchParams,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'
import useDateRange from 'hooks/useDateRange'
import theme from 'lib/theme'
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'
import { Box, Typography } from '@mui/material'
import { formatPrice } from 'lib/utils'
import { OrdersHeader } from './OrdersHeader'
import { CheckboxColumn, FulfilmentColumn, PartIdColumn } from './OrderColumns'

type Id =
  | 'checkbox'
  | 'id'
  | 'customerAddress.name'
  | 'createdAt'
  | 'payment.type'
  | 'totalPrice'
  | 'capturedPrice'
  | 'partId'
  | 'parentOrder'
  | 'fulfilment'
  | 'status'
  | 'placeId'

export const Orders = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [checkedOrders, setCheckedOrders] = useState<
    { id: string; visibleId: string }[]
  >([])
  const locationState = location.state as {
    placeName: string
    placeQuery: string
  }
  const [searchParams] = useSearchParams()
  const pageIndex = searchParams.get('page')
  const entries = searchParams.entries()
  const paramInfo = Array.from(entries).filter((value: string[]) => {
    return value[0] !== 'page' && value[0] !== 'cleared'
  })
  const dateRange = useDateRange()

  const queries =
    paramInfo.length > 0
      ? paramInfo.reduce((acc, param) => {
          if (!param[0].includes('date')) {
            const key = param[0]
            Object.assign(acc, { [key]: param[1] })
          }
          return acc
        }, {} as { place_id: string })
      : null

  const { t } = useTranslation()
  const rowsPerPage = 13

  const cleared = searchParams.get('cleared') === 'true'
  const { error, orders, pagination, isFetching } = useOrders(
    Number(pageIndex) || 1,
    rowsPerPage,
    cleared,
    dateRange[0],
    dateRange[1],
    queries
  )

  const places = orders.reduce((acc, order) => {
    order.parts.forEach((part) => {
      if (!acc.includes(part.placeId)) {
        acc.push(part.placeId)
      }
    })
    return acc
  }, [] as string[])
  if (queries && queries.place_id && !places.includes(queries.place_id)) {
    places.push(queries.place_id)
  }
  const {
    places: placeNames,
    isLoading,
    error: placesError,
  } = useGetUserPlaces(places)

  const getValue = (order: Order, id: string) => {
    const data = id.split('.').reduce((p, prop) => {
      return p[prop as keyof typeof order] as unknown as Order
    }, order)
    if (id === 'checkbox') {
      return (
        <CheckboxColumn
          checkedOrders={checkedOrders}
          order={order}
          setCheckedOrders={setCheckedOrders}
        />
      )
    }
    if (id === 'id') {
      return (
        <Box height='100%' display='flex'>
          {order.visibleId}
        </Box>
      )
    }
    if (id === 'parentOrder') {
      return (
        <Box height='100%' display='flex'>
          <Typography variant='body2'>{order.id}</Typography>
        </Box>
      )
    }
    if (id === 'totalPrice') {
      const totalPrice = Number(order.totalPrice?.totalPriceVat)
      return totalPrice ? formatPrice(totalPrice) : '-'
    }
    if (id === 'capturedPrice') {
      if (order.payment?.status !== 'captured') return '-'
      const capturedPrice = Number(order.capturedPrice?.totalPriceVat)
      return capturedPrice ? formatPrice(capturedPrice) : '-'
    }

    if (id === 'fulfilment') {
      return (
        <>
          {order.parts.map((part) => {
            return <FulfilmentColumn part={part} key={part.id} />
          })}
        </>
      )
    }
    if (id === 'status') {
      return order.parts && order.parts.length > 0 ? (
        <>
          {order.parts.map((part) => (
            <Box minHeight='48px' key={part.id + part.status}>
              {t(`orderStatus.${part.status}`)}
            </Box>
          ))}
        </>
      ) : (
        ''
      )
    }
    if (id === 'createdAt') {
      const dateParts = (data as unknown as string).split(' ')
      const formattedDate = new Date(
        `${dateParts?.[0]}T${dateParts?.[1]}${dateParts?.[2]}`
      )
      if (isValid(formattedDate)) {
        return (
          <Box>
            <Typography>{format(formattedDate, 'd.M.yy')}</Typography>
            <Typography sx={{ color: theme.palette.text.secondary }}>
              {format(formattedDate, 'HH:mm')}
            </Typography>
          </Box>
        )
      }
    }
    if (id === 'partId') {
      return <PartIdColumn order={order} />
    }
    if (id === 'payment.type') {
      return t(`orderPayment.${data}`)
    }
    if (id === 'placeId') {
      if (isLoading || placesError) return ''
      return (
        <>
          {order.parts.map((part) => {
            const placeName = placeNames.filter(
              (place) => place.value === part.placeId
            )[0]
            return (
              <Box minHeight='48px' key={part.id + part.placeId}>
                {placeName?.label}
              </Box>
            )
          })}
        </>
      )
    }
    return data
  }

  const columns: Column<Id>[] = [
    { id: 'checkbox', label: '', minWidth: 10 },
    { id: 'id', label: t('orderNumber'), minWidth: 0 },
    {
      id: 'parentOrder',
      label: t('reference'),
      minWidth: 80,
    },
    { id: 'customerAddress.name', label: t('customer'), minWidth: 150 },
    {
      id: 'createdAt',
      label: t('created'),
      minWidth: 80,
    },
    {
      id: 'payment.type',
      label: t('payment'),
      minWidth: 80,
    },
    {
      id: 'totalPrice',
      label: t('orderValue'),
      minWidth: 80,
    },
    {
      id: 'capturedPrice',
      label: t('captured'),
      minWidth: 80,
    },
    {
      id: 'partId',
      label: t('searchContent.partId'),
      minWidth: 80,
    },
    {
      id: 'fulfilment',
      label: t('fulfilment'),
      minWidth: 100,
    },
    {
      id: 'status',
      label: t('status'),
      minWidth: 120,
    },
    {
      id: 'placeId',
      label: t('from'),
      minWidth: 170,
    },
  ]

  const handleChangePage = (newPage: number) => {
    const searchParams = new URLSearchParams(location.search)
    if (newPage > 0 && newPage <= pagination.totalPages) {
      const formattedParams = createSearchParams({
        ...(cleared && { cleared: 'true' }),
        page: newPage?.toString() || '1',
        ...queries,
      })
      const startDeliveryDate = searchParams.get('start_delivery_date')
      const endDeliveryDate = searchParams.get('end_delivery_date')
      if (startDeliveryDate) {
        formattedParams.append('start_delivery_date', startDeliveryDate)
      }
      if (endDeliveryDate) {
        formattedParams.append('end_delivery_date', endDeliveryDate)
      }
      navigate(`/order?${formattedParams}`, {
        state: locationState,
      })
    }
  }

  const handleNavigate = (id: string) => {
    localStorage.setItem('ordersListingPage', location.search)
    navigate(`/order/${id}/info`)
  }

  if (isFetching || !orders) return <LoadingIndicator />
  if (error) return <h1>Error</h1>

  return (
    <Box p={4}>
      <ListingPage
        alignTop
        tableWidth='calc(100vw - 64px - 40px)'
        handleNavigate={handleNavigate}
        columns={columns}
        listItems={orders}
        query={queries ? 'true' : undefined}
        pagination={pagination}
        header={
          <OrdersHeader
            placeInfo={placeNames}
            checkedOrders={checkedOrders}
            orders={orders}
            setCheckedOrders={setCheckedOrders}
            queries={queries}
            hasListItems={Boolean(orders && orders.length > 0)}
          />
        }
        page={Number(pageIndex) || 1}
        handleChangePage={handleChangePage}
        displayValues={getValue}
      />
    </Box>
  )
}
